/**
 * STARSEED-CAPMARKETS-230 — read a statement screenshot, confirm, fill the rows.
 *
 * The operator's framing, which is the whole design brief:
 *
 *   "i am not able to enter numbers in cells i make mistakes all the time.
 *    Milen always sends screenshots, i have asked for csv but get screenshots."
 *
 * So a file upload does not help — there is no file — and manual entry is the
 * known defect rather than the safe fallback. This reads the figures and asks
 * the operator to CONFIRM a match instead of typing one.
 *
 * Three properties, in the order they matter:
 *
 * 1. **Nothing here writes.** `/partner-statements/propose` is read-only. Rows
 *    land in the editor below and still go through the same dry-run and the
 *    same `POST /partner-statements/import` every other path uses.
 * 2. **The member is chosen by the operator, never deduced.** Ranked against
 *    every account, a real 2026-08-31 block fit an unrelated member's account
 *    within 137 dollars of the right one. The human who received the statement
 *    knows whose it is; the ranking only picks between THAT member's accounts.
 * 3. **A low-confidence match is shown as unresolved, not as a best guess.**
 *    The account select stays empty and the row cannot be applied until someone
 *    picks. "I cannot tell" is a real answer here.
 *
 * What it does NOT do is verify the digits. A misread big enough to matter
 * moves the figure out of its account's band and shows up as a poor match; a
 * small one — 98,992.70 read as 98,992.76 — passes silently. That limit is
 * real, it is on the ticket, and it is still strictly better than a number
 * typed by someone who has said this is the step he gets wrong.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, CheckCircle2, HelpCircle, Upload } from "lucide-react";
import api from "../lib/api";
import { describeMoney } from "../lib/partnerAccountQueue";

const MAX_BYTES = 8 * 1024 * 1024;

const money = (v) => {
  const d = describeMoney(v);
  return d.known ? d.text : "—";
};

const errorText = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  return fallback;
};

/**
 * @param members  [{ id, name }] — the operator picks one; nothing infers it.
 * @param onApply  called with ([{ accountRef, balance, openTradesProfit, proposal }],
 *                 { memberId }) — `proposal` names the upload and block a row
 *                 came from, `memberId` the member its accounts were ranked for.
 */
const StatementScreenshotImport = ({ members = [], onApply }) => {
  const [memberId, setMemberId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [unconfigured, setUnconfigured] = useState(null);
  const [proposals, setProposals] = useState(null);
  // Operator's choice per block. Seeded from the ranking ONLY where the ranking
  // was confident; an unconfident block starts empty so applying it requires a
  // deliberate pick rather than a default nobody looked at.
  const [chosen, setChosen] = useState({});
  // The upload the proposals on screen came from. "Fill in" re-sends every
  // ready block, so the page needs to tell this block, filled in again, from a
  // new figure for the same account (CAPMARKETS-239). A new upload mints a new id.
  const [batch, setBatch] = useState(null);
  // Bumped by every reset, so a read overtaken by a member change or a newer
  // upload is discarded when it lands. Without it a response started before
  // "Whose statement?" changed still filled the importer with proposals ranked
  // for the previous member, stamped with that member — and the page's
  // cross-member question compared against the stale member and stayed silent
  // (CAPMARKETS-239, review of d9143fdd).
  const generation = useRef(0);
  // A read discarded that way is said, not swallowed: switching A→B→A mid-read
  // otherwise leaves the operator waiting for proposals that will never appear.
  // Listed by member, so a late note beside another member's proposals says
  // whose screenshot it was (review of a8dbda49).
  const [discarded, setDiscarded] = useState([]);

  const reset = () => {
    generation.current += 1;
    setProposals(null);
    setChosen({});
    setError(null);
    setUnconfigured(null);
    setBatch(null);
    setBusy(false);
    setDiscarded([]);
  };

  const upload = useCallback(async (file) => {
    if (!file) return;
    reset();
    const mine = generation.current;
    if (!memberId) {
      setError("Choose whose statement this is first.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`That image is ${Math.round(file.size / 1024)}KB; the limit is ${MAX_BYTES / 1024}KB.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("member_id", memberId);
      const { data } = await api.post("/partner-statements/propose", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (mine !== generation.current) {
        setDiscarded((d) => (d.includes(memberId) ? d : [...d, memberId]));
        return;
      }
      setProposals(data.proposals || []);
      setBatch({
        id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        memberId,
      });
      const seed = {};
      (data.proposals || []).forEach((p, i) => {
        if (p.match?.confident && p.match?.best) seed[i] = p.match.best;
      });
      setChosen(seed);
    } catch (e) {
      if (mine !== generation.current) {
        setDiscarded((d) => (d.includes(memberId) ? d : [...d, memberId]));
        return;
      }
      if (e?.response?.status === 503) {
        setUnconfigured(errorText(e, "Screenshot reading is not configured."));
      } else {
        setError(errorText(e, "Could not read that screenshot."));
      }
    } finally {
      if (mine === generation.current) setBusy(false);
    }
  }, [memberId]);

  const readyRows = useMemo(() => {
    if (!proposals) return [];
    return proposals
      .map((p, i) => ({ p, i, ref: chosen[i] }))
      .filter(({ p, ref }) => ref && p.block?.balance !== null && p.block?.balance !== undefined)
      .map(({ p, i, ref }) => ({
        accountRef: String(ref),
        balance: String(p.block.balance),
        // Blank, not "0" — a block with no open-trades line must stay ABSENT so
        // equity comes out unknown rather than fabricated (CAPMARKETS-181).
        openTradesProfit:
          p.block.open_trades_profit === null || p.block.open_trades_profit === undefined
            ? ""
            : String(p.block.open_trades_profit),
        proposal: `${batch?.id}:${i}`,
      }));
  }, [proposals, chosen, batch]);

  return (
    <Card className="border-border">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Read from a screenshot</h2>
            <p className="text-sm text-foreground-muted">
              Upload the partner&apos;s statement image. Figures are read out and matched
              to accounts; you confirm before anything is filled in.
            </p>
          </div>
          <Badge className="bg-background-subtle text-foreground-muted">Writes nothing</Badge>
        </div>

        <div className="flex gap-3 flex-wrap items-end">
          <div className="space-y-1">
            <span className="text-sm font-medium text-foreground">Whose statement?</span>
            <select
              value={memberId}
              onChange={(e) => { setMemberId(e.target.value); reset(); }}
              className="block rounded-md border border-border bg-background-elevated px-3 py-2 text-sm text-foreground"
              data-testid="statement-member-select"
            >
              <option value="">Select a member…</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <label className="inline-flex">
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="sr-only"
              disabled={busy || !memberId}
              onChange={(e) => {
                // Cleared as soon as the file is taken, so choosing the same file
                // again — after a member change, or a discarded read — still fires
                // a change. Browsers fire none for an unchanged value.
                const file = e.target.files?.[0];
                e.target.value = "";
                upload(file);
              }}
              data-testid="statement-file-input"
            />
            <Button asChild={false} type="button" disabled={busy || !memberId}
              onClick={(e) => e.currentTarget.previousSibling?.click()}>
              <Upload className="mr-2 h-4 w-4" />
              {busy ? "Reading…" : "Choose screenshot"}
            </Button>
          </label>
        </div>

        {unconfigured && (
          <div className="rounded-lg border border-border bg-background-subtle p-4 text-sm"
               data-testid="statement-unconfigured">
            <p className="text-foreground font-medium">Screenshot reading isn&apos;t set up yet.</p>
            <p className="text-foreground-muted mt-1">{unconfigured}</p>
            <p className="text-foreground-muted mt-1">
              You can still enter figures by hand below — that is unchanged.
            </p>
          </div>
        )}

        {discarded.length > 0 && (
          <div className="rounded-lg border border-border bg-background-subtle p-4 text-sm text-foreground"
               data-testid="statement-discarded">
            {discarded.length === 1 ? "A screenshot" : "Screenshots"} for{" "}
            <strong>{discarded.map((id) => members.find((m) => m.id === id)?.name || id).join(", ")}</strong>{" "}
            that {discarded.length === 1 ? "was" : "were"} still being read{" "}
            {discarded.length === 1 ? "was" : "were"} discarded, because the member or the upload
            changed while it was read. Upload {discarded.length === 1 ? "it" : "them"} again if you
            still need {discarded.length === 1 ? "it" : "them"}.
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-danger/40 bg-danger/10 p-4 text-sm text-foreground"
               data-testid="statement-error">
            {error}
          </div>
        )}

        {proposals && proposals.length === 0 && (
          <p className="text-sm text-foreground-muted" data-testid="statement-no-blocks">
            No account blocks were found in that image.
          </p>
        )}

        {proposals && proposals.length > 0 && (
          <div className="space-y-3" data-testid="statement-proposals">
            {proposals.map((p, i) => {
              const m = p.match;
              const confident = Boolean(m?.confident);
              const options = m?.candidates || [];
              return (
                <div key={i} className="rounded-lg border border-border p-4 space-y-3"
                     data-testid={`statement-block-${i}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-sm font-medium text-foreground">
                      Block {i + 1}
                      {p.block?.account_hint ? ` · ${p.block.account_hint}` : ""}
                    </span>
                    {confident ? (
                      <Badge className="bg-success/15 text-success border-success/30">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {/* CAPMARKETS-233. This read `m.margin ? … : "Single
                            candidate"`, and `margin` is null for TWO different
                            reasons — an exact match (ratio infinite, which JSON
                            cannot carry) and a member with only one anchored
                            account. So a two-account member whose figure matched
                            to the cent was labelled "Single candidate", which is
                            a false statement about their accounts, on the screen
                            whose whole job is to let someone confirm a match.
                            `exact` and `anchored_count` say which. */}
                        {m.exact
                          ? "Exact match to the last balance"
                          : m.margin
                            ? `${m.margin}× better than the next`
                            : m.anchored_count === 1
                              ? "Only one account to compare"
                              : "Best fit"}
                      </Badge>
                    ) : (
                      <Badge className="bg-warning/15 text-warning border-warning/30">
                        <HelpCircle className="mr-1 h-3 w-3" /> Needs your decision
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-foreground-soft text-xs">Balance</p>
                      <p className="font-mono text-foreground">{money(p.block?.balance)}</p>
                    </div>
                    <div>
                      <p className="text-foreground-soft text-xs">Open trades</p>
                      <p className="font-mono text-foreground">{money(p.block?.open_trades_profit)}</p>
                    </div>
                    <div>
                      <p className="text-foreground-soft text-xs">Equity (derived)</p>
                      <p className="font-mono text-foreground">{money(p.derived_equity)}</p>
                    </div>
                    <div>
                      <p className="text-foreground-soft text-xs">Net profit</p>
                      <p className="font-mono text-foreground">{money(p.block?.net_profit)}</p>
                    </div>
                  </div>

                  {p.block?.reconciles === false && (
                    <p className="flex items-start gap-2 text-xs text-warning"
                       data-testid={`statement-block-${i}-unreconciled`}>
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      Open + closed trades does not equal trade results in this block.
                      Check it against the image before saving — the balance and open-trades
                      figures above are the two that get written.
                    </p>
                  )}

                  <div className="space-y-1">
                    <span className="text-sm font-medium text-foreground">Account</span>
                    <select
                      value={chosen[i] || ""}
                      onChange={(e) => setChosen((c) => ({ ...c, [i]: e.target.value }))}
                      className="block w-full rounded-md border border-border bg-background-elevated px-3 py-2 text-sm text-foreground"
                      data-testid={`statement-block-${i}-account`}
                    >
                      <option value="">Choose an account…</option>
                      {options.map((c) => (
                        <option key={c.partner_account_ref} value={c.partner_account_ref}>
                          {c.partner_account_ref}
                          {c.prior_balance !== null && c.prior_balance !== undefined
                            ? ` — last ${money(c.prior_balance)}${c.gap !== null && c.gap !== undefined ? `, off by ${money(c.gap)}` : ""}`
                            : " — no prior balance"}
                        </option>
                      ))}
                    </select>
                    {m?.reason && (
                      <p className="text-xs text-foreground-soft">{m.reason}</p>
                    )}
                    {!m && p.reason && (
                      <p className="text-xs text-foreground-soft">{p.reason}</p>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
              <p className="text-sm text-foreground-muted">
                {readyRows.length} of {proposals.length} block
                {proposals.length === 1 ? "" : "s"} ready to fill in.
              </p>
              <Button
                type="button"
                disabled={readyRows.length === 0}
                onClick={() => onApply?.(readyRows, { memberId: batch?.memberId || memberId })}
                data-testid="statement-apply"
              >
                Fill in {readyRows.length} row{readyRows.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StatementScreenshotImport;
